/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

declare global {
	interface Window {
		APP_DEBUG: boolean;
	}
}

const _window: Window = globalThis.window || ({} as Window);

/**
 * Logging wrapper
 */
export class BrowserLogger {
	private name: string;

	/**
	 * Logger constructor
	 *
	 * @param name Component name
	 */
	public constructor(name: string) {
		this.name = name;
	}

	/**
	 * Logs the message
	 *
	 * @param logArguments Log arguments
	 */
	public log(...logArguments: Array<unknown>): void {
		console.log(`[${this.name}]`, ...logArguments);
	}

	/**
	 * Logs the error message
	 *
	 * @param logArguments Log arguments
	 */
	public error(...logArguments: Array<unknown>): void {
		console.error(`[${this.name}]`, ...logArguments);
	}

	/**
	 * Logs the warning message
	 *
	 * @param logArguments Log arguments
	 */
	public warn(...logArguments: Array<unknown>): void {
		console.warn(`[${this.name}]`, ...logArguments);
	}

	/**
	 * Logs the info message
	 *
	 * @param logArguments Log arguments
	 */
	public info(...logArguments: Array<unknown>): void {
		console.info(`[${this.name}]`, ...logArguments);
	}

	/**
	 * Logs the debug message
	 *
	 * @param logArguments Log arguments
	 */
	public debug(...logArguments: Array<unknown>): void {
		if (_window.APP_DEBUG) {
			console.debug(`[${this.name}]`, ...logArguments);
		}
	}

	public alert(...logArguments: Array<unknown>): void {
		console.warn(`[${this.name}]`, ...logArguments);
	}

	public notice(...logArguments: Array<unknown>): void {
		console.info(`[${this.name}]`, ...logArguments);
	}

	/**
	 * Creates a logger for a given facility
	 *
	 * @param name Facility name
	 */
	public facility(name: string): BrowserLogger {
		return new BrowserLogger(this.name + "/" + name);
	}
}
